import React, { useState, useEffect } from 'react';
import { useFrappeGetCall, useFrappePostCall } from 'frappe-react-sdk';
import { Calendar, TrendingUp, TrendingDown, Minus, Award, BookOpen, Filter, Search, MessageSquare, Edit2, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';

interface Assessment {
  id: string;
  session: {
    name: string;
    title: string;
    description: string;
    session_date: string;
    student_1_name: string;
    student_2_name: string;
  };
  doctor: string;
  creation: string;
  modified: string;
  scores: {
    total_score: number;
    max_score: number;
    percentage: number;
    average_score: number;
  };
  status: string;
  overall_rating: string;
  evaluator_comments: string;
  category_count: number;
}

interface Survey {
  id: string;
  type: 'survey';
  session_id: string;
  session_title: string;
  user: string;
  creation: string;
  modified: string;
  submission_date: string;
  responses: Record<string, any>;
  response_count: number;
  is_block_survey: boolean;
}

interface AssessmentSummary {
  total_assessments: number;
  completed_assessments: number;
  draft_assessments: number;
  average_score: number;
  best_score: number;
  worst_score: number;
  recent_activity: number;
  improvement_trend: 'improving' | 'declining' | 'stable' | 'insufficient_data';
  monthly_progress: Array<{
    month: string;
    average_score: number;
    assessment_count: number;
  }>;
}

const AssessmentCard: React.FC<{ assessment: Assessment; onViewDetail: (id: string) => void; onEdit: (id: string) => void }> = ({
  assessment,
  onViewDetail,
  onEdit
}) => {
  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600 bg-green-50';
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      'Completed': 'bg-green-100 text-green-800',
      'Draft': 'bg-gray-100 text-gray-800',
      'In Progress': 'bg-blue-100 text-blue-800'
    };

    return statusStyles[status as keyof typeof statusStyles] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Award className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">Assessment</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {assessment.session.title}
          </h3>
          <p className="text-sm text-gray-600 mb-2">
            Session: {assessment.session.name}
          </p>
          <div className="flex items-center text-sm text-gray-500">
            <Calendar className="w-4 h-4 mr-1" />
            {assessment.session.session_date ? new Date(assessment.session.session_date).toLocaleDateString() : 'No date'}
          </div>
        </div>

        <div className="flex flex-col items-end space-y-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(assessment.status)}`}>
            {assessment.status}
          </span>
          <div className={`px-3 py-2 rounded-lg text-center ${getScoreColor(assessment.scores.percentage)}`}>
            <div className="text-lg font-bold">{assessment.scores.percentage?.toFixed(1) || 0}%</div>
            <div className="text-xs">Overall Score</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div>
          <span className="text-gray-500">Students:</span>
          <div className="font-medium">
            {[assessment.session.student_1_name, assessment.session.student_2_name]
              .filter(Boolean)
              .join(', ') || 'Not specified'}
          </div>
        </div>
        <div>
          <span className="text-gray-500">Categories:</span>
          <div className="font-medium">{assessment.category_count} evaluated</div>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Assessed on {new Date(assessment.creation).toLocaleDateString()}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(assessment.id)}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium flex items-center gap-1"
          >
            <Edit2 className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={() => onViewDetail(assessment.id)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};

const SurveyCard: React.FC<{ survey: Survey; onEdit: (id: string) => void }> = ({
  survey,
  onEdit
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare className="w-4 h-4 text-purple-600" />
            <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded">
              {survey.is_block_survey ? 'Training Block Survey' : 'Session Survey'}
            </span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {survey.session_title}
          </h3>
          {survey.session_id !== 'block_survey' && (
            <p className="text-sm text-gray-600 mb-2">
              Session: {survey.session_id}
            </p>
          )}
          <div className="flex items-center text-sm text-gray-500">
            <Calendar className="w-4 h-4 mr-1" />
            Submitted on {new Date(survey.submission_date).toLocaleDateString()}
          </div>
        </div>

        <div className="flex flex-col items-end space-y-2">
          <div className="px-3 py-2 rounded-lg text-center bg-purple-50 text-purple-700">
            <div className="text-lg font-bold">{survey.response_count}</div>
            <div className="text-xs">Responses</div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Last modified {new Date(survey.modified).toLocaleDateString()}
        </div>
        <button
          onClick={() => onEdit(survey.id)}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-medium flex items-center gap-1"
        >
          <Edit2 className="w-4 h-4" />
          Edit Responses
        </button>
      </div>
    </div>
  );
};

const SummaryCard: React.FC<{ title: string; value: string | number; icon: React.ReactNode; trend?: string }> = ({
  title,
  value,
  icon,
  trend
}) => {
  const getTrendIcon = () => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'declining':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      case 'stable':
        return <Minus className="w-4 h-4 text-gray-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="p-2 bg-blue-50 rounded-lg">
            {icon}
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          </div>
        </div>
        {trend && (
          <div className="flex items-center">
            {getTrendIcon()}
          </div>
        )}
      </div>
    </div>
  );
};

export const Assessments: React.FC = () => {
  const [filters, setFilters] = useState({
    search: '',
    session: '',
    dateFrom: '',
    dateTo: '',
    status: '',
    type: 'all' // 'all', 'assessments', 'surveys'
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [editingItem, setEditingItem] = useState<{id: string, type: 'assessment' | 'survey'} | null>(null);

  const pageSize = 10;

  // Fetch assessments
  const { data: assessmentsData, error, isLoading, mutate } = useFrappeGetCall<{
    data: {
      assessments: Assessment[];
      pagination: {
        total: number;
        limit: number;
        offset: number;
        has_more: boolean;
      };
      summary: AssessmentSummary;
    };
  }>('surgical_training.api.user_assessments.get_user_assessments', {
    limit: pageSize,
    offset: currentPage * pageSize,
    session_filter: filters.session || undefined,
    date_from: filters.dateFrom || undefined,
    date_to: filters.dateTo || undefined
  });

  // Fetch surveys
  const { data: surveysData, error: surveysError, isLoading: surveysLoading, mutate: mutateSurveys } = useFrappeGetCall<{
    data: {
      surveys: Survey[];
      pagination: {
        total: number;
        limit: number;
        offset: number;
        has_more: boolean;
      };
    };
  }>('surgical_training.api.user_assessments.get_user_surveys', {
    limit: pageSize,
    offset: currentPage * pageSize,
    session_filter: filters.session || undefined,
    date_from: filters.dateFrom || undefined,
    date_to: filters.dateTo || undefined
  });

  // API calls for editing
  const { call: updateSurvey } = useFrappePostCall('surgical_training.api.user_assessments.update_survey_response');
  const { call: updateAssessment } = useFrappePostCall('surgical_training.api.user_assessments.update_session_evaluation');

  // Fetch available sessions for filter
  const { data: sessionsData } = useFrappeGetCall<{
    data: Array<{
      name: string;
      title: string;
      session_date: string;
    }>;
  }>('surgical_training.api.user_assessments.get_available_sessions_for_assessment');

  // Parse API responses - handle nested Frappe response structure
  const assessmentsResponse = assessmentsData?.data || (assessmentsData as any)?.message?.data || (assessmentsData as any)?.message;
  const surveysResponse = surveysData?.data || (surveysData as any)?.message?.data || (surveysData as any)?.message;

  const assessments = assessmentsResponse?.assessments || [];
  const surveys = surveysResponse?.surveys || [];
  const summary = assessmentsResponse?.summary;
  const assessmentsPagination = assessmentsResponse?.pagination;
  const surveysPagination = surveysResponse?.pagination;
  const availableSessions = sessionsData?.data || [];


  // Combine all items based on filter
  const allItems = React.useMemo(() => {
    let items: (Assessment | Survey)[] = [];

    if (filters.type === 'all' || filters.type === 'assessments') {
      items.push(...assessments.map((a: any) => ({ ...a, itemType: 'assessment' as const })));
    }
    if (filters.type === 'all' || filters.type === 'surveys') {
      items.push(...surveys.map((s: any) => ({ ...s, itemType: 'survey' as const })));
    }

    // Sort by creation date, newest first
    return items.sort((a, b) => new Date(b.creation).getTime() - new Date(a.creation).getTime());
  }, [assessments, surveys, filters.type]);

  // Filter items by search term
  const filteredItems = allItems.filter(item => {
    if (!filters.search) return true;
    const searchLower = filters.search.toLowerCase();

    if ('session' in item) {
      // Assessment
      return (
        item.session.title.toLowerCase().includes(searchLower) ||
        item.session.name.toLowerCase().includes(searchLower) ||
        item.session.student_1_name?.toLowerCase().includes(searchLower) ||
        item.session.student_2_name?.toLowerCase().includes(searchLower)
      );
    } else {
      // Survey
      return (
        item.session_title.toLowerCase().includes(searchLower) ||
        item.session_id.toLowerCase().includes(searchLower)
      );
    }
  });

  const handleViewDetail = (assessmentId: string) => {
    // Navigate to detail view - this would be handled by router
    window.location.href = `/surgical_training/assessments/${assessmentId}`;
  };

  const handleEdit = (id: string, type: 'assessment' | 'survey' = 'assessment') => {
    setEditingItem({ id, type });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  const handleSaveEdit = async (id: string, type: 'assessment' | 'survey', data: any) => {
    try {
      if (type === 'survey') {
        await updateSurvey({
          survey_id: id,
          responses: JSON.stringify(data)
        });
        toast.success('Survey updated successfully');
        mutateSurveys();
      } else {
        await updateAssessment({
          evaluation_id: id,
          evaluation_data: data
        });
        toast.success('Assessment updated successfully');
        mutate();
      }
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating:', error);
      toast.error(`Failed to update ${type}`);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(0); // Reset to first page when filtering
  };

  const handlePageChange = (direction: 'next' | 'prev') => {
    const currentPagination = filters.type === 'surveys' ? surveysPagination :
                              filters.type === 'assessments' ? assessmentsPagination :
                              (assessmentsPagination?.has_more || surveysPagination?.has_more ?
                                { has_more: true } : { has_more: false });

    if (direction === 'next' && currentPagination?.has_more) {
      setCurrentPage(prev => prev + 1);
    } else if (direction === 'prev' && currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  if (isLoading || surveysLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || surveysError) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Data</h2>
            <p className="text-red-600">
              {error?.message || surveysError?.message || 'Failed to load assessments and surveys'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Submissions</h1>
          <p className="text-gray-600">View and edit your training assessments and survey responses</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <SummaryCard
            title="Total Assessments"
            value={summary?.total_assessments || 0}
            icon={<Award className="w-6 h-6 text-blue-600" />}
          />
          <SummaryCard
            title="Survey Responses"
            value={surveys.length}
            icon={<MessageSquare className="w-6 h-6 text-purple-600" />}
          />
          <SummaryCard
            title="Average Score"
            value={`${summary?.average_score?.toFixed(1) || 0}%`}
            icon={<TrendingUp className="w-6 h-6 text-green-600" />}
            trend={summary?.improvement_trend}
          />
          <SummaryCard
            title="Recent Activity"
            value={`${summary?.recent_activity || 0} this month`}
            icon={<Calendar className="w-6 h-6 text-gray-600" />}
          />
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search submissions..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => handleFilterChange('type', 'all')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    filters.type === 'all'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => handleFilterChange('type', 'assessments')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    filters.type === 'assessments'
                      ? 'bg-white text-blue-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Assessments
                </button>
                <button
                  onClick={() => handleFilterChange('type', 'surveys')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    filters.type === 'surveys'
                      ? 'bg-white text-purple-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Surveys
                </button>
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <Filter className="w-4 h-4 mr-2" />
                More Filters
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Session</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={filters.session}
                    onChange={(e) => handleFilterChange('session', e.target.value)}
                  >
                    <option value="">All Sessions</option>
                    {availableSessions.map(session => (
                      <option key={session.name} value={session.name}>
                        {session.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Submissions List */}
        <div className="space-y-4">
          {filteredItems.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No {filters.type === 'all' ? 'Submissions' :
                    filters.type === 'assessments' ? 'Assessments' : 'Surveys'} Found
              </h3>
              <p className="text-gray-600">
                {filters.search || filters.session || filters.dateFrom || filters.dateTo
                  ? "Try adjusting your filters to see more results."
                  : `You haven't completed any ${filters.type === 'all' ? 'submissions' :
                      filters.type === 'assessments' ? 'assessments' : 'surveys'} yet.`}
              </p>
            </div>
          ) : (
            filteredItems.map(item => (
              'session' in item ? (
                <AssessmentCard
                  key={item.id}
                  assessment={item}
                  onViewDetail={handleViewDetail}
                  onEdit={(id) => handleEdit(id, 'assessment')}
                />
              ) : (
                <SurveyCard
                  key={item.id}
                  survey={item}
                  onEdit={(id) => handleEdit(id, 'survey')}
                />
              )
            ))
          )}
        </div>

        {/* Pagination */}
        {((assessmentsPagination && assessmentsPagination.total > pageSize) ||
          (surveysPagination && surveysPagination.total > pageSize)) && (
          <div className="flex justify-between items-center mt-8">
            <div className="text-sm text-gray-600">
              Showing items from page {currentPage + 1}
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange('prev')}
                disabled={currentPage === 0}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange('next')}
                disabled={!(assessmentsPagination?.has_more || surveysPagination?.has_more)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingItem && <EditModal
          itemId={editingItem.id}
          itemType={editingItem.type}
          onSave={handleSaveEdit}
          onCancel={handleCancelEdit}
        />}
      </div>
    </div>
  );
};

// Edit Modal Component
const EditModal: React.FC<{
  itemId: string;
  itemType: 'assessment' | 'survey';
  onSave: (id: string, type: 'assessment' | 'survey', data: any) => void;
  onCancel: () => void;
}> = ({ itemId, itemType, onSave, onCancel }) => {
  const [editData, setEditData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch the data to edit
  const fetchApiUrl = itemType === 'survey'
    ? 'surgical_training.api.user_assessments.get_survey_detail'
    : 'surgical_training.api.user_assessments.get_assessment_detail';

  const fetchParams = itemType === 'survey'
    ? { survey_id: itemId }
    : { assessment_id: itemId };

  const { data, error } = useFrappeGetCall(fetchApiUrl, fetchParams);

  useEffect(() => {
    // Handle Frappe's nested response structure
    const responseData = data?.data || data?.message?.data || data?.message;
    if (responseData) {
      setEditData(responseData);
      setIsLoading(false);
    } else if (error) {
      setIsLoading(false);
    }
  }, [data, error]);

  const handleSave = async () => {
    setIsSaving(true);
    await onSave(itemId, itemType, editData);
    setIsSaving(false);
  };

  const handleFieldChange = (field: string, value: any) => {
    setEditData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResponseChange = (questionId: string, value: string) => {
    setEditData((prev: any) => ({
      ...prev,
      responses: {
        ...prev.responses,
        [questionId]: value
      }
    }));
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded mb-4 w-3/4"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || (!isLoading && (!editData || Object.keys(editData).length === 0))) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
          <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Data</h3>
          <p className="text-red-600 mb-4">
            {(error as any)?.message || (error as any)?.error || 'Failed to load submission data. Please try again.'}
          </p>
          <div className="text-sm text-gray-600 mb-4">
            <p>Debug info:</p>
            <p>• Item ID: {itemId}</p>
            <p>• Item Type: {itemType}</p>
            <p>• API: {itemType === 'survey' ? 'get_survey_detail' : 'get_assessment_detail'}</p>
          </div>
          <button
            onClick={onCancel}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Edit {itemType === 'survey' ? 'Survey Responses' : 'Assessment'}
              </h2>
              <p className="text-sm text-gray-600">
                {itemType === 'survey' ? editData.session_title : editData.session?.title}
              </p>
            </div>
            <button
              onClick={onCancel}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {itemType === 'survey' ? (
            <div className="space-y-6 pb-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-medium text-purple-900 mb-2">Survey Responses</h3>
                <p className="text-sm text-purple-700">
                  Edit your responses to the survey questions. Changes will be saved when you click Save.
                </p>
              </div>

              {Object.entries(editData.responses || {}).map(([questionId, response]) => (
                <div key={questionId} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Question {questionId}
                  </label>
                  <textarea
                    value={response as string}
                    onChange={(e) => handleResponseChange(questionId, e.target.value)}
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-vertical"
                    placeholder="Enter your response..."
                  />
                </div>
              ))}

              {Object.keys(editData.responses || {}).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No responses found for this survey.
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6 pb-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">Assessment Details</h3>
                <p className="text-sm text-blue-700">
                  Edit the assessment scores and comments. Note that changes to scores may affect overall ratings.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Overall Rating</label>
                  <input
                    type="text"
                    value={editData.overall_rating || ''}
                    onChange={(e) => handleFieldChange('overall_rating', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Excellent, Good, Satisfactory"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={editData.status || ''}
                    onChange={(e) => handleFieldChange('status', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Draft">Draft</option>
                    <option value="Completed">Completed</option>
                    <option value="In Progress">In Progress</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Overall Comments</label>
                <textarea
                  value={editData.evaluator_comments || ''}
                  onChange={(e) => handleFieldChange('evaluator_comments', e.target.value)}
                  rows={6}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                  placeholder="Enter overall assessment comments..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer - Fixed */}
        <div className="flex-shrink-0 border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-6 py-2 rounded-md transition-colors flex items-center gap-2 ${
                itemType === 'survey'
                  ? 'bg-purple-600 hover:bg-purple-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Assessments;